import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { Form, Popup } from 'semantic-ui-react';
import { Reports, Rates, RoomTypes } from '../../../api/services/next';
import { useCurrentHotel } from '../../../hooks/useCurrentHotel';
import { useMessageResources, useTextResources } from '../../../language';
import GridSearchTemplate from '../../../pages/GridSearchTemplate';
import CheckboxWrapper from '../../shared/Checkbox';
import { DatePicker } from '../../shared/DatePicker';
import HotelsDropDown from '../../shared/HotelsDropDown';
import RatesDropDown from '../../shared/RatesDropDown';
import RoomTypesDropDown from '../../shared/RoomTypesDropDown';
import { weekDays } from '../../shared/constants';
import { onWarning } from '../../shared/messages';
import {
  formatValueAsCurrency,
  makeIntToDaysOfWeekArray,
  formatMomentDateRange,
  formatDate,
} from '../../shared/utils';
import Weekdays from '../../shared/Weekdays';
import { endpoint } from "../../../api/http"

const RoomTypeFinalPrices = () => {
  const currentHotel = useCurrentHotel();
  const [search, setSearch] = useState({});
  const [rates, setRates] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomTypeIds, setSelectedRoomTypeIds] = useState([])

  const text = useTextResources();
  const msg = useMessageResources();

  const fromDate = search['FromToDates[0]'];
  const toDate = search['FromToDates[1]'];

  useEffect(() => {
    if (search.HotelId && fromDate && toDate) {

        const roomTypesSpec = {
            HotelId: search.HotelId,
            FromDate: fromDate,
            ToDate: toDate,
        };
        RoomTypes.GetRoomTypes(roomTypesSpec).then(setRoomTypes);

        const ratesSpec = {
          HotelId: search.HotelId,
          FromDate: fromDate,
          ToDate: toDate,
          InheritedRates: true,
          GetAllInheritingRates: true,
          RoomTypeIds: selectedRoomTypeIds,
        };
        Rates.GetRates(ratesSpec).then(setRates);
    } else {
      setRates([]);
      setRoomTypes([]);
    }
  }, [fromDate, search.HotelId, toDate, selectedRoomTypeIds]);

  const getDatesRangeCellValue = useCallback((row, columnName) => {
    const range = row[columnName];
    return range ? moment.range(moment(range[0]), moment(range[1])) : null;
  }, []);

  const DatesRangeCell = useCallback(({ value }) => {
    return value ? formatMomentDateRange(value) : "-";
  }, []);

  const datesRangeComparer = useCallback((rangeA, rangeB) => {
    return rangeA.start.valueOf() - rangeB.start.valueOf();
  }, []);

  const getRateNameCellValue = useCallback((row) => {
    return row.RateId && row.RateName ? {
      RateId: row.RateId,
      RateName: row.RateName,
    } : null;
  }, []);

  const RateNameCell = useCallback(({ value }) => {
    return (
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${endpoint}PMS/pricelist/Edit?RateId=${value.RateId}`}
      >
        {value.RateName}
      </a>
    );
  }, []);

  const rateNameGroupCriteria = useCallback((value) => {
    return { key: value.RateId, value };
  }, []);

  const getHiddenRateNameForExcelCellValue = useCallback((row) => {
    return row.RateName;
  }, []);

  const getHiddenStartDateForExcelCellValue = useCallback((row) => {
    const startDate = row?.DatesRange[0];
    return  startDate ? formatDate(startDate) : '-';
  }, []);

  const getHiddenEndDateForExcelCellValue = useCallback((row) => {
    const endDate = row?.DatesRange[1];
    return  endDate ? formatDate(endDate) : '-';
  }, []);

  const getTemplateNameCellValue = useCallback((row) => {
    const templateName = row?.TemplateName;
    return  templateName ?? '-';
  }, []);

  const getFormattedCurrencyOrPercentCellValue = useCallback((row, columnName) => {
    const value = row[columnName];
    if (value === undefined) {
      return '-';
    }
    return row.IsPercent
      ? `${value}%`
      : formatValueAsCurrency(value, row.CurrencyId);
  }, []);

  const WeekdaysCell = useCallback(({ value }) => {
    return <Weekdays selectedWeekdaysInt={value} onlyChosenDays />;
  }, []);

  const weekDaysIntToString = useCallback((value) => {
    const arrayOfWeekdays = makeIntToDaysOfWeekArray(value);
    const result = arrayOfWeekdays
      .map((dayIndex) => text(weekDays[dayIndex]))
      .join(', ');
    return result;
  }, [text]);

  const getHiddenWeekdaysForExcelCellValue = useCallback((row) => {
    const value = row.Weekdays;
    return weekDaysIntToString(value);
  }, [weekDaysIntToString]);

  const weekDaysPredicate = useCallback((value, filter) => {
    const weekDaysString = weekDaysIntToString(value);
    return weekDaysString.toLowerCase().includes(filter.value.toLowerCase());
  }, [weekDaysIntToString]);

  const toolTipFormatter = (value) => {
    return <Popup content={value} trigger={<span>{value}</span>} />;
  };

  const customizeFooter = worksheet => {
    const rows = worksheet._rows.filter((value, index) => index !== 1);
    worksheet._rows = [];
    rows.forEach((row) => worksheet.addRow(row.values));
  }

  const columns = useMemo(() => [
    {
      name: 'HiddenRateNameForExcel',
      title: text('RateName'),
      getCellValue: getHiddenRateNameForExcelCellValue,
    },
    {
      name: 'HiddenStartDateForExcel',
      title: text('StartDate'),
      getCellValue: getHiddenStartDateForExcelCellValue,
    },
    {
      name: 'HiddenEndDateForExcel',
      title: text('EndDate'),
      getCellValue: getHiddenEndDateForExcelCellValue,
    },
    {
      name: 'DatesRange',
      title: text('DatesRange'),
      getCellValue: getDatesRangeCellValue,
      formatter: DatesRangeCell,
    },
    {
      name: 'Weekdays',
      title: text('Weekdays'),
      formatter: WeekdaysCell,
    },
    {
      name: 'RateName',
      title: text('Rate'),
      getCellValue: getRateNameCellValue,
      formatter: RateNameCell,
    },
    {
      name: 'HiddenWeekdaysForExcel',
      title: text('Weekdays'),
      getCellValue: getHiddenWeekdaysForExcelCellValue,
    },
    {
      name: 'RoomType',
      title: text('RoomType'),
    },
    {
      name: 'PricingTemplate',
      title: text('PricingTemplate'),
      getCellValue: getTemplateNameCellValue,
    },
    {
      name: 'PerRoom',
      title: text('Room'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'Single',
      title: text('Single'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'Double',
      title: text('Double'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FirstExtraAdult',
      title: text('FirstExtraAdult'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'SecondExtraAdult',
      title: text('SecondExtraAdult'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'ThirdExtraAdult',
      title: text('ThirdExtraAdult'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FourthExtraAdult',
      title: text('FourthExtraAdult'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FirstExtraChild',
      title: text('FirstExtraChild'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'SecondExtraChild',
      title: text('SecondExtraChild'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'ThirdExtraChild',
      title: text('ThirdExtraChild'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FourthExtraChild',
      title: text('FourthExtraChild'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FirstExtraInfant',
      title: text('FirstExtraInfant'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'SecondExtraInfant',
      title: text('SecondExtraInfant'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'ThirdExtraInfant',
      title: text('ThirdExtraInfant'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FourthExtraInfant',
      title: text('FourthExtraInfant'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'FixBasePrice',
      title: text('FixBasePrice'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'BasePersonAmount',
      title: text('BasePersonAmount'),
      getCellValue: getFormattedCurrencyOrPercentCellValue
    },
    {
      name: 'ExtraPerson',
      title: text('ExtraPerson'),
      getCellValue: getFormattedCurrencyOrPercentCellValue
    },
  ].map(c => ({ ...c, exportTitle: c.title, title: toolTipFormatter(c.title) })),
    [DatesRangeCell, RateNameCell, WeekdaysCell, getDatesRangeCellValue, getFormattedCurrencyOrPercentCellValue, getHiddenRateNameForExcelCellValue, getHiddenWeekdaysForExcelCellValue, getRateNameCellValue, text]);

  const gridOptions = useMemo(() => ({
    search: {
      title: text('RoomTypeFinalPrices'),
      fields: [
        <Form.Field
          key='HotelId'
          label={text('Hotel')}
          control={HotelsDropDown}
        />,
        <DatePicker
          clearable={true}
          picktype='range'
          label={text('Dates')}
          key='FromToDates'
        />,
        <Form.Field
          label={text('RoomTypes')}
          control={RoomTypesDropDown}
          data={roomTypes}
          key='RoomTypeIds'
          placeholder={text('All')}
          value={selectedRoomTypeIds}
          onChange={setSelectedRoomTypeIds}
          clearable
          width='4'
          allowSelectAll
          multiple
          search
        />,
        <Form.Field
          label={text('Rates')}
          control={RatesDropDown}
          data={rates}
          key='RateIds'
          placeholder={text('All')}
          clearable
          width='4'
          allowSelectAll
          multiple
          search
        />,
        <CheckboxWrapper label={text('IncludeVAT')} key='PriceIncludesVat' />,
      ],
      api: Reports.RoomTypeFinalPrices,
      initial: {
        ...(currentHotel && { HotelId: currentHotel.Id }),
      },
      searchSpec: {
        MaxResults: 1000,
      },
      validate: (state) => {
        let fromDate = state['FromToDates[0]'];
        let toDate = state['FromToDates[1]'];
        if (!fromDate) onWarning(msg('FromDateIsRequired'));
        if (!toDate) onWarning(msg('ToDateIsRequired'));
        return Boolean(fromDate && toDate);
      },
    },
    grid: {
      columns,
      noVirtualTable: true,
      paging: true,
      customOptions: {
        overrideData: true,
        sort: {
          sorting: [
            { columnName: 'RateName', direction: 'asc' },
            { columnName: 'DatesRange', direction: 'asc' },
          ],
          compare: [
            { columnName: 'DatesRange', compare: datesRangeComparer },
          ],
        },
        group: {
          grouping: [
            { columnName: 'RateName' },
          ],
          extensions: [
            { columnName: 'RateName', groupingEnabled: false },
          ],
          criteria: [
            { columnName: 'RateName', criteria: rateNameGroupCriteria },
          ],
        },
        hidden: {
          hiddenColumns: [
            { columnName: 'HiddenWeekdaysForExcel' },
            { columnName: 'HiddenRateNameForExcel' },
            { columnName: 'HiddenStartDateForExcel' },
            { columnName: 'HiddenEndDateForExcel' },
          ],
        },
      },
      scrollbarX: true,
      columnExtensions: [
        { columnName: 'RateName', width: 170 },
        { columnName: 'DatesRange', width: 180 },
        { columnName: 'Weekdays', width: 260 },
        { columnName: 'RoomType', width: 170 },
      ],
      export: true,
      excel: {
        customizeFooter: customizeFooter,
        hiddenColumns: [
          { columnName: 'Weekdays' },
          { columnName: 'DatesRange' },
        ],
      },
      filtering: {
        extensions: [
          {
            columnName: 'Weekdays',
            predicate: weekDaysPredicate,
          },
        ],
      },
    },
  }), [columns, currentHotel, datesRangeComparer, msg, rateNameGroupCriteria, rates, roomTypes, text, weekDaysPredicate, selectedRoomTypeIds]);

  return (
    <div style={{ 'overflowX': 'auto' }}>
      <GridSearchTemplate
        options={gridOptions}
        onSearchChange={setSearch}
      />
    </div>
  );
};

export default RoomTypeFinalPrices;
